import type { RouteRecordRaw } from 'vue-router';
import { Authenticated, ModelBindings, Require2FA, ServerInstalled } from '~/middlewares';
import { useModelsStore } from '~/stores';

export const serverRoutes: RouteRecordRaw[] = [
    // Server blocked route - separate from main server routes to avoid ModelBindings
    {
        path: '/server/:server/blocked',
        name: 'server.blocked',
        component: () => import('~/views/errors/ServerError.vue'),
        meta: {
            middlewares: [Authenticated],
        },
    },
    {
        path: '/server/:server',
        component: () => import('~/views/GenericLayout.vue'),
        meta: {
            middlewares: [Authenticated, Require2FA, new ModelBindings('client'), ServerInstalled],
            showChildrenInNavbar: true,
        },
        children: [
            {
                name: 'server.system.index',
                path: '',
                component: () => import('~/views/server/Index.vue'),
                meta: {
                    icon: 'server',
                },
            },
            {
                name: 'server.rust.live-map',
                path: 'live-map',
                component: () => import('~/views/server/rust/pages/RustGodTools.vue'),
                props: { page: 'map' },
                meta: {
                    icon: 'map',
                    feature: 'server:rust-live-map',
                    permission: 'server:rust_live_map.access',
                    stretch: true,
                    noBreadcrumbs: true,
                    disableNumberFlow: true,
                    fullscreen: true,
                    noPadding: true,
                },
            },
            {
                name: 'server.rust.players',
                path: 'players',
                component: () => import('~/views/server/rust/pages/RustGodTools.vue'),
                props: { page: 'players' },
                meta: {
                    icon: 'users',
                    feature: 'server:rust-players',
                    permission: 'server:control.command',
                    stretch: true,
                    noBreadcrumbs: true,
                    noPadding: true,
                },
            },
            {
                name: 'server.rust.permissions',
                path: 'rust-permissions/:tab?',
                component: () => import('~/views/server/rust/permissions/Index.vue'),
                meta: {
                    feature: 'server:rust-permissions',
                    icon: 'user-shield',
                    permission: 'server:rust_permissions.read',
                },
            },
            {
                name: 'server.tools.grafana',
                path: 'metrics',
                component: () => import('~/views/server/grafana/Index.vue'),
                meta: {
                    icon: 'chart-line',
                    feature: 'server:grafana',
                    permission: 'server:grafana.read',
                    stretch: true,
                },
            },
            {
                name: 'server.sbox-packages',
                path: 'sbox-packages',
                component: () => import('~/views/server/sbox/pages/PackageBrowser.vue'),
                meta: {
                    icon: 'cube',
                    permission: 'server:startup.update',
                    feature: 'server:sbox-package-browser',
                },
                children: [
                    {
                        name: 'server.sbox-packages.show',
                        path: ':fullIdent',
                        component: () => import('~/views/server/sbox/pages/PackageDetail.vue'),
                        meta: {
                            icon: 'cube',
                            permission: 'server:startup.update',
                            feature: 'server:sbox-package-browser',
                            hidden: true,
                            noBreadcrumbs: true,
                        },
                    },
                ],
            },
            {
                name: 'server.management.files',
                path: 'files',
                component: () => import('~/views/Passthrough.vue'),
                redirect: { name: 'server.management.files.index' },
                meta: {
                    icon: 'folder-open',
                    permission: 'server:file.list',
                    passthrough: true,
                },
                children: [
                    {
                        name: 'server.management.files.index',
                        path: '',
                        component: () => import('~/views/server/files/FilesIndex.vue'),
                        meta: {
                            icon: 'folder-open',
                            permission: 'server:file.list',
                            stretch: true,
                        },
                    },
                    {
                        name: 'server.management.files.new',
                        path: 'new',
                        component: () => import('~/views/server/files/Manage.vue'),
                        meta: {
                            icon: 'file-alt',
                            permission: 'server:file.write',
                            stretch: true,
                        },
                    },
                    {
                        name: 'server.management.files.edit',
                        path: 'edit',
                        component: () => import('~/views/server/files/Manage.vue'),
                        meta: {
                            icon: 'file-alt',
                            permission: 'server:file.read',
                            stretch: true,
                        },
                    },
                ],
            },
            {
                name: 'server.configuration.palworld',
                path: 'palworld-server-config',
                component: () => import('~/views/server/configurators/palworld/Configurator.vue'),
                meta: {
                    icon: 'pen-to-square',
                    feature: 'server:configurator-palworld',
                },
            },
            {
                name: 'server.configuration.hytale',
                path: 'hytale-server-config',
                component: () => import('~/views/server/configurators/hytale/Configurator.vue'),
                meta: {
                    icon: 'pen-to-square',
                    feature: 'server:configurator-hytale',
                },
            },
            {
                name: 'server.configuration.vrising',
                path: 'vrising-server-config',
                component: () => import('~/views/server/configurators/vrising/Configurator.vue'),
                meta: {
                    icon: 'pen-to-square',
                    feature: 'server:configurator-vrising',
                },
            },
            {
                name: 'server.configuration.rust_server_configurator',
                path: 'rust-server-config',
                component: () => import('~/views/server/configurators/rust/Configurator.vue'),
                meta: {
                    icon: 'pen-to-square',
                    permission: 'server:file.delete',
                    feature: 'server:configurator-rust',
                },
            },
            {
                name: 'server.configuration.minecraft_sever_properties',
                path: 'minecraft-server-config',
                component: () => import('~/views/server/configurators/minecraft/Configurator.vue'),
                meta: {
                    icon: 'pen-to-square',
                    permission: 'server:file.delete',
                    feature: 'server:configurator-minecraft',
                },
            },
            {
                name: 'server.configuration.gmod_server_configurator',
                path: 'gmod-server-config',
                component: () => import('~/views/server/configurators/garrysmod/Configurator.vue'),
                meta: {
                    icon: 'pen-to-square',
                    permission: 'server:file.delete',
                    feature: 'server:configurator-gmod',
                },
            },
            {
                // Physgun gets the Codefling browser promoted to its own top-level
                // entry; everyone else keeps the addon-gated grid installer under
                // Tools, see server.tools.plugin-browser. Gated on the Codefling
                // feature, not the grid's -- Codefling costs no addon, only physgun.
                name: 'server.plugin-installer',
                path: 'plugin-installer',
                component: () => import('~/views/server/rust/pages/PluginBrowser.vue'),
                meta: {
                    icon: 'plug',
                    permission: 'server:game_mod.read',
                    feature: 'server:codefling-browser',
                    addon: 'physgun',
                },
                children: [
                    {
                        name: 'server.plugin-installer.package',
                        path: 'codefling/:package',
                        component: () => import('~/views/server/codefling/pages/PackageDetail.vue'),
                        meta: {
                            icon: 'puzzle-piece',
                            permission: 'server:game_mod.read',
                            feature: 'server:codefling-browser',
                            addon: 'physgun',
                            hidden: true,
                            noBreadcrumbs: true,
                        },
                    },
                ],
            },
            {
                // Startup parameters and the config editor are the same job,
                // "change how this server is set up", so they are tabs of one
                // page rather than two entries competing in the sidebar.
                // passthrough collapses both into a single sidebar entry;
                // tabRoot is what TabNavigation reads to derive the tabs.
                name: 'server.configuration.startup',
                path: 'startup',
                component: () => import('~/views/layouts/SegmentedTabsLayout.vue'),
                redirect: { name: 'server.configuration.startup.index' },
                meta: {
                    icon: 'sliders-h',
                    permission: 'server:startup.read',
                    passthrough: true,
                    tabRoot: true,
                },
                children: [
                    {
                        name: 'server.configuration.startup.index',
                        path: '',
                        component: () => import('~/views/server/startup/Index.vue'),
                        meta: {
                            icon: 'sliders-h',
                            permission: 'server:startup.read',
                            // The sidebar names the container of both tabs and reads
                            // this child's title, so the tab needs its own narrower
                            // label or both would say "Server Options".
                            tabLabel: 'navigation.server.configuration.startup.index.tab_title',
                        },
                    },
                    {
                        name: 'server.configuration.startup.config_editor',
                        path: 'config-editor',
                        component: () => import('~/views/server/config-editor/Index.vue'),
                        meta: {
                            icon: 'pen-to-square',
                            permission: 'server:file.read',
                            feature: 'server:config-editor',
                            addon: 'physgun',
                        },
                    },
                    {
                        // Same job as the other two tabs -- "change how this
                        // server is set up". It used to live on Server Actions
                        // beside the support toggle and reinstall, which is
                        // where you go to *do something to* a server, not to
                        // change what it runs; nobody looking to switch their
                        // Minecraft version would think to look there.
                        name: 'server.configuration.startup.edition',
                        path: 'edition',
                        component: () => import('~/views/server/edition-installer/Index.vue'),
                        meta: {
                            icon: 'layer-group',
                            // Matches TriggerVersionInstallRequest: installing an
                            // edition reinstalls the server, so it is gated on
                            // reinstall rather than on startup.read like its siblings.
                            permission: 'server:reinstall.update',
                            feature: 'server:version-manager',
                        },
                    },
                ],
            },
            {
                name: 'server.configuration.switch_game',
                path: 'switch-game',
                component: () => import('~/views/server/switch-game/Index.vue'),
                meta: {
                    icon: 'exchange-alt',
                    permission: 'server:startup.switch_game',
                    feature: 'server:switch-game',
                },
            },
            {
                name: 'server.configuration.splitter',
                path: 'splitter',
                component: () => import('~/views/server/splitter/Index.vue'),
                meta: {
                    icon: 'layer-group',
                    permission: 'server:split.read',
                    feature: 'server:splitter',
                },
            },
            {
                name: 'server.system',
                path: '',
                component: () => import('~/views/Passthrough.vue'),
                meta: {
                    icon: 'computer',
                    showAsCategory: true,
                },
                children: [
                    // Leads the group deliberately: the rest of System is read-only
                    // reference, so the one thing users come here to *act* on goes first.
                    {
                        name: 'server.system.schedules',
                        path: 'schedules',
                        component: () => import('~/views/Passthrough.vue'),
                        redirect: { name: 'server.system.schedules.index' },
                        meta: {
                            icon: 'calendar-alt',
                            permission: 'server:schedule.read',
                        },
                        children: [
                            {
                                name: 'server.system.schedules.index',
                                path: '',
                                component: () => import('~/views/server/schedules/Index.vue'),
                                meta: {
                                    icon: 'calendar-alt',
                                    permission: 'server:schedule.read',
                                },
                            },
                            {
                                name: 'server.system.schedules.manage',
                                path: ':schedule',
                                component: () => import('~/views/server/schedules/Manage.vue'),
                                meta: {
                                    icon: 'calendar-alt',
                                },
                            },
                        ],
                    },
                    {
                        name: 'server.system.details',
                        path: 'details',
                        component: () => import('~/views/server/details/Index.vue'),
                        meta: {
                            icon: 'chart-area',
                            permission: 'server:details.read',
                        },
                    },
                    {
                        name: 'server.system.sftp',
                        path: 'sftp',
                        component: () => import('~/views/server/sftp/Index.vue'),
                        meta: {
                            icon: 'file-upload',
                            permission: 'server:file.sftp',
                            // delisted for now, remove later in the future
                            hidden: true,
                        },
                    },
                    {
                        name: 'server.system.ssh',
                        path: 'ssh',
                        component: () => import('~/views/server/ssh/Index.vue'),
                        meta: {
                            icon: 'code',
                            permission: 'server:ssh.connect',
                            feature: 'server:ssh',
                        },
                    },
                    {
                        name: 'server.system.audit_logs',
                        path: 'audit-logs',
                        component: () => import('~/views/server/audit-logs/Index.vue'),
                        meta: {
                            icon: 'fingerprint',
                            permission: 'server:audit.read',
                        },
                    },
                ],
            },
            {
                name: 'server.management',
                path: '',
                component: () => import('~/views/Passthrough.vue'),
                meta: {
                    icon: 'people-roof',
                    showAsCategory: true,
                },
                children: [
                    {
                        name: 'server.management.databases',
                        path: 'databases',
                        component: () => import('~/views/server/databases/Index.vue'),
                        meta: {
                            icon: 'database',
                            permission: 'server:database.read',
                        },
                    },
                    {
                        name: 'server.management.backups',
                        path: 'backups',
                        component: () => import('~/views/Passthrough.vue'),
                        redirect: { name: 'server.management.backups.index' },
                        meta: {
                            icon: 'boxes',
                            permission: 'server:backup.read',
                            passthrough: true,
                        },
                        children: [
                            {
                                name: 'server.management.backups.index',
                                path: '',
                                component: () => import('~/views/server/backups/Index.vue'),
                                meta: {
                                    icon: 'boxes',
                                    permission: 'server:backup.read',
                                },
                            },
                            {
                                name: 'server.management.backups.explore',
                                path: ':backup/explore',
                                component: () => import('~/views/server/backups/explore/BackupExplore.vue'),
                                meta: {
                                    icon: 'folder-tree',
                                    permission: ['backup.read', 'file.list', 'file.read'],
                                    feature: 'server:backup-explore',
                                    ln: 'v2',
                                    stretch: true,
                                    hidden: true,
                                },
                            },
                        ],
                    },
                    {
                        name: 'server.management.subusers.index',
                        path: 'subusers',
                        component: () => import('~/views/server/subusers/Index.vue'),
                        meta: {
                            icon: 'users',
                            permission: 'server:subuser.read',
                        },
                    },
                ],
            },
            {
                name: 'server.configuration',
                path: '',
                component: () => import('~/views/Passthrough.vue'),
                meta: {
                    icon: 'cog',
                    showAsCategory: true,
                },
                children: [
                    {
                        name: 'server.configuration.advanced',
                        path: 'advanced',
                        component: () => import('~/views/server/advanced/Index.vue'),
                        meta: {
                            icon: 'cogs',
                        },
                    },
                    {
                        name: 'server.configuration.disk_usage',
                        path: 'disk-analyzer',
                        component: () => import('~/views/server/disk-usage/Index.vue'),
                        meta: {
                            icon: 'chart-pie',
                            feature: 'server:disk-analyzer',
                            permission: 'server:file.list',
                            stretch: true,
                        },
                    },
                    {
                        name: 'server.configuration.git',
                        path: 'git',
                        component: () => import('~/views/server/git/GitOverview.vue'),
                        meta: {
                            icon: 'code-branch',
                            permission: 'server:file.git',
                            feature: 'server:git',
                        },
                    },
                    {
                        name: 'server.configuration.importer',
                        path: 'import',
                        component: () => import('~/views/server/importer/ImportWizard.vue'),
                        meta: {
                            icon: 'file-import',
                            feature: 'server:importer',
                        },
                    },
                    {
                        name: 'server.configuration.allocations',
                        path: 'allocations',
                        component: () => import('~/views/server/allocations/Index.vue'),
                        meta: {
                            icon: 'network-wired',
                            permission: 'server:allocation.read',
                        },
                    },
                ],
            },
            {
                name: 'server.tools',
                path: '',
                component: () => import('~/views/Passthrough.vue'),
                meta: {
                    icon: 'wrench',
                    showAsCategory: true,
                },
                children: [
                    {
                        name: 'server.tools.domains',
                        path: 'domains',
                        component: () => import('~/views/server/domains/Index.vue'),
                        meta: {
                            icon: 'globe',
                            // Mirrors the backend's either-of grant: a web-only
                            // subuser must still reach their custom domains.
                            permission: ['server:subdomain.read', 'server:reverse_proxy.read'],
                            permissionAny: true,
                            feature: ['server:domain-manager', 'server:reverse-proxy'],
                        },
                    },
                    {
                        name: 'server.tools.rust-favorites',
                        path: 'rust-favorites',
                        component: () => import('~/views/server/subdomains/RustFavoritesPanel.vue'),
                        meta: {
                            icon: 'cubes',
                            permission: 'server:subdomain.read',
                            feature: 'server:rust-favorites-dns',
                        },
                    },
                    {
                        name: 'server.tools.wipe',
                        path: 'wipe',
                        component: () => import('~/views/server/wipe/Index.vue'),
                        meta: {
                            permission: 'server:rustwipe.trigger',
                            icon: 'cogs',
                            feature: 'server:rust-wipe',
                        },
                    },
                    {
                        name: 'server.tools.palworld-save-converter',
                        path: 'palworld-save-converter',
                        component: () => import('~/views/server/palworld/SaveConverter.vue'),
                        meta: {
                            feature: 'server:palworld-save-converter',
                            permission: 'server:file.write',
                            icon: 'exchange-alt',
                        },
                    },
                    {
                        name: 'server.tools.modpacks',
                        path: 'modpacks',
                        component: () => import('~/views/server/modpacks/Index.vue'),
                        meta: {
                            icon: 'cubes',
                            permission: 'server:modpack.read',
                            feature: 'server:modpack-manager',
                        },
                    },
                    {
                        name: 'server.tools.plugins',
                        path: 'plugins',
                        component: () => import('~/views/server/plugins/Index.vue'),
                        meta: {
                            icon: 'cogs',
                            permission: 'server:game_mod.read',
                            feature: 'server:plugin-manager',
                        },
                    },
                    {
                        // Physgun promotes this to the top-level Plugin Installer
                        // entry instead, see server.plugin-installer.
                        name: 'server.tools.plugin-browser',
                        path: 'plugin-browser',
                        component: () => import('~/views/server/rust/pages/PluginBrowser.vue'),
                        meta: {
                            icon: 'plug',
                            permission: 'server:game_mod.read',
                            feature: 'server:rust-plugin-browser',
                            hideWhenAddon: 'physgun',
                        },
                    },
                    {
                        name: 'server.tools.hytale-plugin-browser',
                        path: 'hytale-plugin-browser',
                        component: () => import('~/views/server/hytale/pages/PluginBrowser.vue'),
                        meta: {
                            icon: 'plug',
                            permission: 'server:game_mod.read',
                            feature: 'server:hytale-plugin-browser',
                        },
                    },
                    {
                        name: 'server.tools.mods',
                        path: 'mods',
                        component: () => import('~/views/server/mods/Index.vue'),
                        meta: {
                            icon: 'plug',
                            permission: 'server:mod.read',
                            feature: 'server:mod-manager',
                        },
                    },
                    {
                        name: 'server.tools.backdoor_scanner',
                        path: 'backdoor-scanner',
                        component: () => import('~/views/server/backdoor-scanner/Index.vue'),
                        meta: {
                            icon: 'door-open',
                            permission: 'server:scanner.update',
                            feature: 'server:backdoor-scanner',
                        },
                    },
                    {
                        name: 'server.tools.loading_screen',
                        path: 'loading-screen',
                        component: () => import('~/views/server/loading-screen/Index.vue'),
                        meta: {
                            icon: 'sync',
                            feature: 'server:loading-screen',
                        },
                    },
                    {
                        name: 'server.tools.fastdl',
                        path: 'fastdl',
                        component: () => import('~/views/server/fastdl/Index.vue'),
                        meta: {
                            icon: 'download',
                            permission: 'server:fastdl.read',
                            feature: 'server:fastdl',
                        },
                    },
                ],
            },
            {
                name: 'server.help',
                path: '',
                component: () => import('~/views/Passthrough.vue'),
                meta: {
                    icon: 'life-ring',
                    showAsCategory: true,
                },
                children: [
                    {
                        name: 'server.help.discord',
                        path: 'discord',
                        component: () => import('~/views/ExternalRedirect.vue'),
                        meta: {
                            icon: 'fab-discord',
                            feature: 'server:physgun-links',
                            externalUrl: 'https://discord.gg/physgun',
                            fallbackRoute: { name: 'index' },
                        },
                    },
                    {
                        name: 'server.help.knowledgebase',
                        path: 'knowledgebase',
                        component: () => import('~/views/ExternalRedirect.vue'),
                        meta: {
                            icon: 'book',
                            feature: 'server:physgun-links',
                            externalUrl: 'https://help.physgun.com',
                            fallbackRoute: { name: 'index' },
                        },
                    },
                    {
                        name: 'server.help.ticket',
                        path: 'ticket',
                        component: () => import('~/views/ExternalRedirect.vue'),
                        meta: {
                            icon: 'ticket',
                            feature: 'server:physgun-links',
                            externalUrl: 'https://billing.physgun.com/submitticket.php',
                            fallbackRoute: { name: 'index' },
                        },
                    },
                ],
            },
            {
                name: 'server.gmodstore',
                path: 'gmodstore',
                component: () => import('~/views/ExternalRedirect.vue'),
                meta: {
                    icon: 'cart-shopping',
                    feature: 'server:physgun-gmodstore',
                    externalUrl: 'https://gmodstore.com?ref=physgun_gp',
                    fallbackRoute: { name: 'index' },
                },
            },
            {
                name: 'server.hire_a_developer',
                path: 'magic',
                component: () => import('~/views/ExternalRedirect.vue'),
                meta: {
                    icon: 'code',
                    feature: 'server:physgun-hire-a-developer',
                    externalUrl: 'https://phs.gg/magic?ref=gcp',
                    fallbackRoute: { name: 'index' },
                },
            },
            {
                name: 'server.whmcs',
                path: 'whmcs',
                component: () => import('~/views/ExternalRedirect.vue'),
                meta: {
                    icon: 'file-invoice',
                    feature: 'server:physgun-links',
                    whmcsOnly: true,
                    externalUrl: 'whmcs',
                    fallbackRoute: { name: 'index' },
                },
            },
            {
                name: 'server.administrate',
                path: 'administrate',
                component: () => import('~/views/Passthrough.vue'),
                beforeEnter: () => {
                    const modelsStore = useModelsStore();
                    return {
                        name: 'admin.management.servers.manage.about',
                        params: { server: modelsStore.server?.getSluggedRouteID() },
                    };
                },
                meta: {
                    icon: 'shield-alt',
                    adminOnly: true,
                    permission: 'admin:server.read',
                    allowWhenInstalling: true,
                },
            },
            {
                name: 'server.whmcs.admin',
                path: 'whmcs',
                component: () => import('~/views/ExternalRedirect.vue'),
                meta: {
                    icon: 'shield',
                    adminOnly: true,
                    whmcsOnly: true,
                    externalUrl: 'whmcs-admin',
                    fallbackRoute: { name: 'index' },
                },
            },
        ],
    },
    {
        path: '/server/:server/console',
        component: () => import('~/views/PopupLayout.vue'),
        meta: {
            middlewares: [Authenticated, Require2FA, new ModelBindings('client')],
        },
        children: [
            {
                name: 'server.console',
                path: '',
                component: () => import('~/views/server/Console.vue'),
            },
        ],
    },
];
